import React, { useState } from 'react';
import {
  Box,
  ButtonBase,
  Menu,
  MenuItem,
  Typography
} from '@mui/material';
import { ArrowDropDown as CaretIcon } from '@mui/icons-material';
import UserAvatar from '../common/UserAvatar';

const UnassignedCircle = ({ size = 28 }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: '50%',
      border: '1.5px dashed rgba(0,0,0,0.3)',
      color: 'rgba(0,0,0,0.4)',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: size * 0.45,
      fontWeight: 600,
      flexShrink: 0
    }}
  >
    ?
  </Box>
);

/**
 * Custom assignee combobox showing colored user avatars (Tasks PRD §8.3).
 * Locked (cursor: not-allowed, click no-op) while the task is private —
 * private tasks are always assigned to their creator.
 *
 * Props:
 * - users: [{ uid, name, color }] (active users)
 * - value: assignee uid or null/'' for unassigned
 * - onChange(uid | null)
 * - locked: boolean
 * - usersByUid: full user map for resolving inactive/deleted assignees
 */
const AssigneePicker = ({ users, value, onChange, locked = false, usersByUid = {} }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const selected = value
    ? users.find((u) => u.uid === value) ||
      (usersByUid[value] ? { uid: value, ...usersByUid[value] } : null)
    : null;

  const handleOpen = (e) => {
    if (locked) return;
    setAnchorEl(e.currentTarget);
  };

  const handleSelect = (uid) => {
    onChange(uid);
    setAnchorEl(null);
  };

  return (
    <>
      <ButtonBase
        onClick={handleOpen}
        disableRipple={locked}
        sx={{
          width: '100%',
          justifyContent: 'flex-start',
          gap: 1.25,
          px: 1.75,
          py: 1.1,
          border: '1px solid rgba(0,0,0,0.23)',
          borderRadius: 1,
          bgcolor: locked ? 'rgba(0,0,0,0.04)' : '#fff',
          cursor: locked ? 'not-allowed' : 'pointer',
          textAlign: 'left',
          '&:hover': locked ? {} : { borderColor: 'text.primary' }
        }}
      >
        {selected ? (
          <UserAvatar name={selected.name} color={selected.color} size={28} />
        ) : (
          <UnassignedCircle />
        )}
        <Typography
          variant="body1"
          sx={{ flex: 1, color: selected ? 'text.primary' : 'text.secondary' }}
          noWrap
        >
          {selected ? selected.name : 'Unassigned'}
        </Typography>
        <CaretIcon sx={{ color: 'rgba(0,0,0,0.54)', opacity: locked ? 0.3 : 1 }} />
      </ButtonBase>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{ sx: { maxHeight: 280, minWidth: anchorEl?.offsetWidth } }}
      >
        <MenuItem onClick={() => handleSelect(null)} sx={{ gap: 1.25 }}>
          <UnassignedCircle />
          <Typography variant="body2" color="text.secondary">
            Unassigned
          </Typography>
        </MenuItem>
        {users.map((user) => (
          <MenuItem key={user.uid} onClick={() => handleSelect(user.uid)} sx={{ gap: 1.25 }}>
            <UserAvatar name={user.name} color={user.color} size={28} />
            <Typography variant="body2">{user.name}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default AssigneePicker;
export { UnassignedCircle };
